import React, { useMemo } from 'react';

import { Box, Stack, Typography } from '@mui/material';

import FieldGrid from '../../shared/FieldGrid';
import ReadOnlyField from '../../shared/ReadOnlyField';

import { getDetailFieldKey } from './detailFieldKeys';
import type { DetailField, DetailSectionId } from './types';

interface DetailFieldGroup {
  columns: 1 | 2 | 3 | 4;
  fields: Array<{ field: DetailField; index: number }>;
  title?: string;
}

interface Props {
  activeSectionId: DetailSectionId;
  draftFieldValues: Record<string, string>;
  fields: Array<DetailField>;
  setDraftFieldValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
}

function buildFieldGroups(fields: Array<DetailField>): Array<DetailFieldGroup> {
  return fields.reduce<Array<DetailFieldGroup>>((groups, field, index) => {
    const title = field.layout?.group;
    const columns = field.layout?.columns ?? 3;
    const lastGroup = groups[groups.length - 1];

    if (groups.length > 0 && lastGroup.title === title && lastGroup.columns === columns) {
      lastGroup.fields.push({ field, index });
      return groups;
    }

    groups.push({ columns, fields: [{ field, index }], title });
    return groups;
  }, []);
}

function getFieldGridColumn(field: DetailField) {
  if (field.layout?.span === 'full') {
    return '1 / -1';
  }

  if (field.layout?.column) {
    return {
      md: String(field.layout.column),
      xl: String(field.layout.column),
    };
  }

  return undefined;
}

function hasExplicitColumns(group: DetailFieldGroup) {
  return group.fields.some(({ field }) => Boolean(field.layout?.column));
}

function getColumnFieldBottomSpacing(field: DetailField, nextField?: DetailField) {
  if (!nextField) {
    return 0;
  }

  if (field.layout?.compact && nextField.layout?.compact) {
    return 1.5;
  }

  return 6;
}

function renderEditableField({
  activeSectionId,
  draftFieldValues,
  field,
  index,
  setDraftFieldValues,
}: {
  activeSectionId: DetailSectionId;
  draftFieldValues: Record<string, string>;
  field: DetailField;
  index: number;
  setDraftFieldValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
}) {
  const fieldKey = getDetailFieldKey(activeSectionId, `${field.label}:${index}`);

  return (
    <ReadOnlyField
      field={field}
      value={draftFieldValues[fieldKey] ?? field.value}
      onChange={
        field.readOnly
          ? undefined
          : (value) =>
              setDraftFieldValues((currentDraftFieldValues) => ({
                ...currentDraftFieldValues,
                [fieldKey]: value,
              }))
      }
    />
  );
}

export default function DetailsFieldsPanel({ activeSectionId, draftFieldValues, fields, setDraftFieldValues }: Props) {
  const fieldGroups = useMemo(() => buildFieldGroups(fields), [fields]);

  return (
    <Stack spacing={3.5} sx={{ pb: 4, pt: 3 }}>
      {fieldGroups.map((group, groupIndex) => (
        <Box key={`${group.title ?? 'fields'}:${groupIndex}`}>
          {group.title && (
            <Typography color="text.secondary" fontWeight={600} sx={{ mb: 1.5 }} variant="subtitle2">
              {group.title}
            </Typography>
          )}
          {hasExplicitColumns(group) ? (
            <FieldGrid columns={group.columns}>
              {Array.from({ length: group.columns }, (_unusedColumn, columnIndex) => {
                const columnFields = group.fields.filter(({ field }) => field.layout?.column === columnIndex + 1);

                return (
                  <Stack key={columnIndex}>
                    {columnFields.map(({ field, index }, fieldIndex) => {
                      const fieldKey = getDetailFieldKey(activeSectionId, `${field.label}:${index}`);
                      const nextField = columnFields[fieldIndex + 1]?.field;
                      const bottomSpacing = getColumnFieldBottomSpacing(field, nextField);

                      if (field.layout?.inline && columnFields[fieldIndex - 1]?.field.layout?.inline) {
                        return null;
                      }

                      if (field.layout?.inline) {
                        const inlineFields = columnFields.filter(({ field }) => field.layout?.inline);
                        const nextNonInlineField = columnFields
                          .slice(fieldIndex + 1)
                          .find(({ field }) => !field.layout?.inline)?.field;

                        return (
                          <Box
                            key={fieldKey}
                            sx={{
                              columnGap: 1.5,
                              display: 'flex',
                              flexWrap: 'wrap',
                              mb: nextNonInlineField ? 6 : 0,
                              rowGap: 1,
                            }}
                          >
                            {inlineFields.map(({ field, index }) => (
                              <Box key={getDetailFieldKey(activeSectionId, `${field.label}:${index}`)}>
                                {renderEditableField({
                                  activeSectionId,
                                  draftFieldValues,
                                  field,
                                  index,
                                  setDraftFieldValues,
                                })}
                              </Box>
                            ))}
                          </Box>
                        );
                      }

                      return (
                        <Box key={fieldKey} sx={{ mb: bottomSpacing }}>
                          {renderEditableField({
                            activeSectionId,
                            draftFieldValues,
                            field,
                            index,
                            setDraftFieldValues,
                          })}
                        </Box>
                      );
                    })}
                  </Stack>
                );
              })}
            </FieldGrid>
          ) : (
            <FieldGrid columns={group.columns}>
              {group.fields.map(({ field, index }, groupFieldIndex) => {
                const fieldKey = getDetailFieldKey(activeSectionId, `${field.label}:${index}`);

                if (field.layout?.inline && group.fields[groupFieldIndex - 1]?.field.layout?.inline) {
                  return null;
                }

                if (field.layout?.inline) {
                  const inlineFields = group.fields.filter(({ field }) => field.layout?.inline);

                  return (
                    <Box
                      key={fieldKey}
                      sx={{
                        alignItems: 'center',
                        columnGap: 1.5,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gridColumn: '1 / -1',
                        rowGap: 1,
                      }}
                    >
                      {inlineFields.map(({ field, index }) => (
                        <Box key={getDetailFieldKey(activeSectionId, `${field.label}:${index}`)}>
                          {renderEditableField({
                            activeSectionId,
                            draftFieldValues,
                            field,
                            index,
                            setDraftFieldValues,
                          })}
                        </Box>
                      ))}
                    </Box>
                  );
                }

                return (
                  <Box key={fieldKey} sx={{ gridColumn: getFieldGridColumn(field) }}>
                    {renderEditableField({
                      activeSectionId,
                      draftFieldValues,
                      field,
                      index,
                      setDraftFieldValues,
                    })}
                  </Box>
                );
              })}
            </FieldGrid>
          )}
        </Box>
      ))}
    </Stack>
  );
}
